import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { authCode, confirmPass, login } from './paths';
import { logoutUser, setRefreshedToken } from '../redux/slices/userProfile';
import { addSnackbar } from '../redux/slices/snackbar';
import { store } from '../redux/store';
import { history } from '../App';
import * as Sentry from '@sentry/browser';
import localStorageKeys from '../constants/localStorageKeys';
import { version } from '../../package.json';

const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
const UNAUTHORIZED = 401;
const FORBIDDEN = 403;
const PERMISSION_CODE = 'permission_denied';
let versionNotified = false;

const { dispatch } = store;

// Cache configuration
interface CacheEntry {
  data: AxiosResponse;
  timestamp: number;
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    cache?: boolean;
    cacheTTL?: number;
  }
}

const cache = new Map<string, CacheEntry>();
// 5 minutes
const DEFAULT_CACHE_TTL = Number(process.env.REACT_APP_REQUEST_CACHE_TTL_MS) || 5 * 60 * 1000;
const isCacheEnabled = process.env.REACT_APP_REQUEST_CACHE_ENABLED === 'true';

const generateCacheKey = (config: AxiosRequestConfig): string => {
  const { method = 'get', url, params, data } = config;
  return JSON.stringify({ method, url, params, data });
};

const isCacheValid = (entry: CacheEntry, ttl: number): boolean => {
  return Date.now() - entry.timestamp < ttl;
};

export const clearApiCache = (urlPattern?: string) => {
  if (!urlPattern) {
    cache.clear();
    return;
  }

  const keysToDelete: string[] = [];
  cache.forEach((_, key) => {
    if (key.includes(urlPattern)) {
      keysToDelete.push(key);
    }
  });

  keysToDelete.forEach((key) => cache.delete(key));
};

const api = axios.create({
  baseURL: baseApiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

const cacheAdapter = (config: AxiosRequestConfig): Promise<AxiosResponse> => {
  const defaultAdapter = axios.defaults.adapter;

  // cache for GET requests only
  if (isCacheEnabled && config.method?.toLowerCase() === 'get' && config.cache) {
    const cacheKey = generateCacheKey(config);
    const cachedEntry = cache.get(cacheKey);
    const ttl = config.cacheTTL || DEFAULT_CACHE_TTL;

    if (cachedEntry && isCacheValid(cachedEntry, ttl)) {
      return Promise.resolve(cachedEntry.data);
    }
  }

  return (defaultAdapter as any)(config).then((response: AxiosResponse) => {
    if (isCacheEnabled && config.cache && config.method?.toLowerCase() === 'get') {
      const cacheKey = generateCacheKey(config);
      cache.set(cacheKey, {
        data: response,
        timestamp: Date.now(),
      });
    }
    return response;
  });
};

api.defaults.adapter = cacheAdapter;

api.interceptors.response.use(
  (response) => {
    // Version check — notify once per session so the dialog doesn't
    // re-appear on every API call after the user dismisses it.
    const uiVersion = response.headers?.['x-ui-version'];
    if (uiVersion && uiVersion !== version && !versionNotified) {
      versionNotified = true;
      window.postMessage({
        key: 'new_version_available',
        value: true,
      }, '*');
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest.url.toString();

    if (!error.response) return Promise.reject(error);

    const { status, data: { code } } = error.response;

    if ((status === FORBIDDEN || status === UNAUTHORIZED) && !originalRequest._retry) {
      if (code === PERMISSION_CODE) {
        history.replace('/');

        return Promise.reject(error);
      }

      if (status === FORBIDDEN && (url.endsWith(login) || url.endsWith(authCode) || url.endsWith(confirmPass))) {
        return Promise.reject(error);
      }

      const refresh_token = store.getState().user.tokens.refresh;
      try {
        const refreshTokenApi = axios.create({
          baseURL: baseApiUrl,
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const tokens = await refreshTokenApi.post(`${login}token_refresh/`, {
          refresh: refresh_token,
        });

        dispatch(setRefreshedToken({
          access: tokens.data.access,
          refresh: tokens.data.refresh,
        }));

        originalRequest._retry = true;

        originalRequest.headers.Authorization = `Bearer ${tokens.data.access}`;
        api.defaults.headers.common.Authorization = `Bearer ${tokens.data.access}`;

        return api(originalRequest);
      } catch (err: any) {
        localStorage.removeItem(localStorageKeys.viprToken);
        localStorage.removeItem(localStorageKeys.viprRefreshToken);
        localStorage.removeItem(localStorageKeys.viprWSToken);
        localStorage.removeItem(localStorageKeys.permissions);
        clearApiCache();
        dispatch(logoutUser());
        Sentry.setUser(null);
        // If user is on login page - we don't want to show session message
        if (history.location.pathname !== '/') {
          dispatch(addSnackbar({ message: 'Session expired. Please log in again.' as unknown as string }));
        }
        history.replace('/');
      }
    }
    return Promise.reject(error);
  },
);

export default api;
